import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { RouterModule } from "@angular/router";
import { boostThemeEnum } from "@app/services/page.service";
import { AngularSvgIconModule } from "angular-svg-icon";

/**
 * A reusable display widget component that provides a themed container with optional expand/collapse functionality.
 * This component is used throughout the application to display content in a consistent, themed manner.
 */
@Component({
  selector: "app-display-widget",
  templateUrl: "./display-widget.component.html",
  styleUrls: ["./display-widget.component.scss"],
  standalone: true,
  imports: [CommonModule, MatButtonModule, AngularSvgIconModule, RouterModule],
})
export class DisplayWidgetComponent implements OnInit, OnChanges {
  /** The theme to apply to the widget's header. Must be a valid boostThemeEnum value or theme string */
  @Input() theme: boostThemeEnum | string;

  /** The theme to apply to the widget's content area. Must be a valid boostThemeEnum value or theme string */
  @Input() contentTheme: boostThemeEnum | string;

  /** The title text to display in the widget's header */
  @Input() title: string = "";

  /** Whether to show expand/collapse functionality. Defaults to false */
  @Input() useExpandFeatures: boolean = false;

  /** Whether to apply a border to the content area. Defaults to false */
  @Input() applyContentBorder: boolean = false;

  /** The opacity level for the content background (0-100). Defaults to 50 */
  @Input() contentOpacity: number = 100;

  /** Event emitter that fires when the expand/collapse state changes */
  @Output() expandToggle = new EventEmitter<boolean>();

  @Input() oNCollapseShowNothing: boolean = false;

  /** Initial expanded state for the widget */
  @Input() initialExpanded: boolean = true;

  /** Tracks the current expanded state of the widget */
  isExpanded: boolean = true;

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialExpanded'] !== undefined) {
      this.isExpanded = changes['initialExpanded'].currentValue;
    }
  }

  ngOnInit() {
    this.isExpanded = this.initialExpanded;
  }

  /**
   * Toggles the expanded state of the widget and emits the new state.
   * Only used when useExpandFeatures is true.
   */
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    this.expandToggle.emit(this.isExpanded);
  }
}
